/**
 * Width budgets for wrapping content text — legend entries, module labels, headings,
 * body copy. Axis labels are budgeted separately in `./axis-label-budget`.
 *
 * This file is the single home for these numbers. It imports nothing, so anything in
 * `components/` or `types/` can depend on it without a cycle.
 */
export const CONTENT_LABEL_BUDGET_CH = {
  /** Text directly under a glyph or logo tile — one or two words. */
  caption: 12,
  /** Inline key of a key/value row, or a chart legend entry. */
  label: 20,
  /** Title or sub-line of a grid cell, where several sit side by side. */
  cell: 24,
  /** Title of a row that spans the module width. */
  row: 32,
  /** Headline of a list item or an FAQ question. */
  heading: 64,
  /** Body copy under a heading. */
  body: 72,
} as const;

export type ContentLabelBudget = keyof typeof CONTENT_LABEL_BUDGET_CH;

type WrapClass<K extends ContentLabelBudget> =
  `max-w-[${(typeof CONTENT_LABEL_BUDGET_CH)[K]}ch] [overflow-wrap:anywhere]`;

/**
 * Each entry pairs its `ch` budget with `[overflow-wrap:anywhere]`, because a budget
 * without it clips instead of wrapping. The class strings are spelled out rather than
 * composed from `CONTENT_LABEL_BUDGET_CH` so Tailwind's source scan still sees them; the
 * `WrapClass` type is what keeps the two in step.
 */
export const CONTENT_LABEL_WRAP: { [K in ContentLabelBudget]: WrapClass<K> } = {
  caption: "max-w-[12ch] [overflow-wrap:anywhere]",
  label: "max-w-[20ch] [overflow-wrap:anywhere]",
  cell: "max-w-[24ch] [overflow-wrap:anywhere]",
  row: "max-w-[32ch] [overflow-wrap:anywhere]",
  heading: "max-w-[64ch] [overflow-wrap:anywhere]",
  body: "max-w-[72ch] [overflow-wrap:anywhere]",
};
